import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { api, setCsrfToken } from "../../lib/api.js";
import { useAuth } from "../../lib/auth-context.js";

export const Route = createFileRoute("/_public/login")({
  component: LoginPage,
});

const API_URL = import.meta.env.VITE_API_URL ?? "";

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  // TOTP step state. When non-null, we render the second-factor form
  // instead of the password form. The pending token expires after 5
  // minutes server-side; if the user takes longer they re-login.
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState("");

  // If the Google callback bounced us back with an error, show it once.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ge = params.get("google_error");
    if (ge) setError(`Google sign-in failed (${ge}).`);
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post<{
        csrfToken?: string;
        requiresTotp?: boolean;
        pendingToken?: string;
      }>("/auth/login", { email, password });
      if (res.data.requiresTotp && res.data.pendingToken) {
        setPendingToken(res.data.pendingToken);
        return;
      }
      if (res.data.csrfToken) setCsrfToken(res.data.csrfToken);
      await refresh();
      navigate({ to: "/dashboard" });
    } catch (err: unknown) {
      type ApiErr = { response?: { data?: { message?: string } } };
      const msg = (err as ApiErr).response?.data?.message ?? "Login failed";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleTotpSubmit(e: FormEvent) {
    e.preventDefault();
    if (!pendingToken || !totpCode.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post<{ csrfToken: string }>("/auth/totp/verify", {
        pendingToken,
        code: totpCode.trim(),
      });
      setCsrfToken(res.data.csrfToken);
      await refresh();
      navigate({ to: "/dashboard" });
    } catch (err: unknown) {
      type ApiErr = { response?: { data?: { message?: string } } };
      const msg =
        (err as ApiErr).response?.data?.message ?? "Invalid code — try again";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell" style={{ background: "var(--accent)" }}>
      <header className="auth-header" style={{ background: "transparent" }}>
        <div className="auth-header-inner">
          <a href="/" className="auth-logo">
            <span className="auth-logo-mark">O!</span>
            <span className="auth-logo-text">oyereply</span>
          </a>
          <a href="/" className="auth-header-link">
            ← Back to home
          </a>
        </div>
      </header>

      <main className="auth-grid signin bg-dot" style={{ flex: 1 }}>
        <section
          className="auth-promo signin"
          style={{ alignSelf: "center" }}
        >
          <span className="stamp" style={{ background: "var(--paper)" }}>
            Welcome back
          </span>
          <h1 className="auth-display-h1 lg" style={{ marginTop: 20 }}>
            Your DMs
            <br />
            missed you. <span className="text-stroke">Probably.</span>
          </h1>
          <p
            style={{
              marginTop: 24,
              fontSize: 17,
              maxWidth: 440,
              color: "rgba(14,14,14,0.82)",
            }}
          >
            A small army of auto-replies has been holding the fort. Sign in to
            see what your followers said today.
          </p>

          <div className="stats-card tilt-l">
            <div
              className="font-mono"
              style={{
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "0.18em",
                color: "rgba(14,14,14,0.55)",
                marginBottom: 10,
              }}
            >
              While you were away
            </div>
            <div className="row">
              <span style={{ fontSize: 14 }}>Replies sent</span>
              <span className="num">1,284</span>
            </div>
            <div className="row">
              <span style={{ fontSize: 14 }}>New leads captured</span>
              <span className="num">147</span>
            </div>
            <div className="row">
              <span style={{ fontSize: 14 }}>Hours saved (est.)</span>
              <span className="num">19.5h</span>
            </div>
          </div>
        </section>

        <section className="auth-form-panel" style={{ alignSelf: "center" }}>
          <div className="auth-form-inner">
            <div className="auth-form-card">
              <h2
                className="font-display"
                style={{
                  margin: 0,
                  fontSize: 36,
                  fontWeight: 800,
                  letterSpacing: "-0.03em",
                }}
              >
                Sign in
              </h2>
              <p
                style={{
                  margin: "6px 0 0",
                  fontSize: 14,
                  color: "rgba(14,14,14,0.7)",
                }}
              >
                New here?{" "}
                <Link
                  to="/signup"
                  className="font-display"
                  style={{ fontWeight: 700, textUnderlineOffset: 4 }}
                >
                  Create an account →
                </Link>
              </p>

              {error && <div className="brut-error">{error}</div>}

              {pendingToken ? (
                <form onSubmit={handleTotpSubmit} style={{ marginTop: 16 }}>
                  <p>
                    Enter the 6-digit code from your authenticator app, or one
                    of your backup codes.
                  </p>
                  <label className="brut-field-label" htmlFor="totp">
                    Verification code
                  </label>
                  <input
                    id="totp"
                    type="text"
                    className="brut-input-2"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="btn-primary press"
                    disabled={submitting || !totpCode.trim()}
                    style={{ marginTop: 20 }}
                  >
                    {submitting ? "Verifying…" : "Continue →"}
                  </button>
                </form>
              ) : (
                <>
                  <div style={{ marginTop: 22 }}>
                    <a
                      href={`${API_URL}/auth/google/start`}
                      className="btn-google press"
                    >
                      <GoogleG />
                      Continue with Google
                    </a>
                  </div>

                  <div className="divider-or">
                    <span className="line" />
                    <span className="label">or with email</span>
                    <span className="line" />
                  </div>

                  <form onSubmit={handleSubmit}>
                    <label className="brut-field-label" htmlFor="email">
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      className="brut-input-2"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@brand.co"
                      autoComplete="email"
                      required
                    />

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        marginTop: 14,
                        marginBottom: 4,
                      }}
                    >
                      <span
                        className="font-display"
                        style={{ fontWeight: 700, fontSize: 13 }}
                      >
                        Password
                      </span>
                      <Link
                        to="/forgot-password"
                        className="font-mono"
                        style={{ fontSize: 11 }}
                      >
                        Forgot?
                      </Link>
                    </div>
                    <input
                      id="password"
                      type="password"
                      className="brut-input-2"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      required
                    />

                    <button
                      type="submit"
                      className="btn-primary press"
                      disabled={submitting}
                      style={{ marginTop: 24 }}
                    >
                      {submitting ? "Signing in…" : "Sign in →"}
                    </button>
                  </form>

                  <p className="fine-print" style={{ marginTop: 20 }}>
                    Protected by 2FA · audited quarterly · zero password resets
                    stored.
                  </p>
                </>
              )}
            </div>

            <p
              style={{
                marginTop: 20,
                textAlign: "center",
                fontSize: 13,
                color: "rgba(14,14,14,0.7)",
              }}
            >
              By signing in you agree to our{" "}
              <a href="/terms" style={{ textUnderlineOffset: 4 }}>
                Terms
              </a>{" "}
              and{" "}
              <a href="/privacy" style={{ textUnderlineOffset: 4 }}>
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285f4"
        d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.4c-.2 1.3-.9 2.4-2 3.1v2.6h3.3c1.9-1.8 3-4.4 3-7.5z"
      />
      <path
        fill="#34a853"
        d="M12 22c2.7 0 5-.9 6.7-2.4l-3.3-2.6c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3v2.6C4.7 19.7 8.1 22 12 22z"
      />
      <path
        fill="#fbbc05"
        d="M6.4 13.9c-.2-.6-.3-1.3-.3-1.9s.1-1.3.3-1.9V7.5H3a10 10 0 0 0 0 9l3.4-2.6z"
      />
      <path
        fill="#ea4335"
        d="M12 6c1.5 0 2.8.5 3.8 1.5l2.9-2.9C17 2.9 14.7 2 12 2 8.1 2 4.7 4.3 3 7.5l3.4 2.6C7.2 7.8 9.4 6 12 6z"
      />
    </svg>
  );
}
